import { formatSouthAfricaDateTime } from "./projectFormDateTime";

// Leaders often build one availability question per date, so a form can hold
// dozens of single-slot questions. Volunteers shouldn't have to scroll
// through them one by one: this flattens every availability slot in a form
// into one chronological list, grouped by month and day (in SAST), with the
// state each slot needs for display.
export const buildAvailabilitySessions = (fields = [], answers = {}, now = new Date()) => {
  const sessions = fields
    .filter((field) => field.type === "availability")
    .flatMap((field) => {
      const picked = new Set((answers[field._id] || []).map(String));
      return (field.slots || []).map((slot) => {
        const capacity = Number(slot.capacity) || 0;
        const taken = Number(slot.taken) || 0;
        const selected = picked.has(String(slot._id));
        // `taken` includes this volunteer's own saved pick, so a session they
        // already hold never shows as full to them.
        const remaining = capacity > 0 ? Math.max(0, capacity - taken) : null;
        return {
          slot,
          fieldId: field._id,
          selected,
          past: new Date(slot.startTime) <= now,
          full: remaining === 0 && !selected,
          remaining,
          capacity,
        };
      });
    })
    .sort((a, b) => new Date(a.slot.startTime) - new Date(b.slot.startTime));

  const months = [];
  sessions.forEach((session) => {
    const monthKey = formatSouthAfricaDateTime(session.slot.startTime, "yyyy-MM");
    const dayKey = formatSouthAfricaDateTime(session.slot.startTime, "yyyy-MM-dd");
    let month = months[months.length - 1];
    if (!month || month.key !== monthKey) {
      month = {
        key: monthKey,
        label: formatSouthAfricaDateTime(session.slot.startTime, "MMMM yyyy"),
        days: [],
      };
      months.push(month);
    }
    let day = month.days[month.days.length - 1];
    if (!day || day.key !== dayKey) {
      day = {
        key: dayKey,
        weekday: formatSouthAfricaDateTime(session.slot.startTime, "EEE"),
        date: formatSouthAfricaDateTime(session.slot.startTime, "d MMM"),
        sessions: [],
      };
      month.days.push(day);
    }
    day.sessions.push(session);
  });

  return {
    sessions,
    months,
    activities: [...new Set(sessions.map((session) => session.slot.activity).filter(Boolean))],
    selectedCount: sessions.filter((session) => session.selected).length,
    pastCount: sessions.filter((session) => session.past).length,
    upcomingCount: sessions.filter((session) => !session.past).length,
  };
};

// Same list, narrowed to what the volunteer asked to see. Past sessions they
// had already picked stay visible so a saved choice never silently vanishes.
export const filterAvailabilityMonths = (months, { showPast = false, activity = "" } = {}) =>
  months
    .map((month) => ({
      ...month,
      days: month.days
        .map((day) => ({
          ...day,
          sessions: day.sessions.filter(
            (session) =>
              (showPast || !session.past || session.selected) &&
              (!activity || session.slot.activity === activity),
          ),
        }))
        .filter((day) => day.sessions.length),
    }))
    .filter((month) => month.days.length);

export const WEEKDAYS = [
  [1, "Mon"],
  [2, "Tue"],
  [3, "Wed"],
  [4, "Thu"],
  [5, "Fri"],
  [6, "Sat"],
  [0, "Sun"],
];
export const MAX_REPEAT_SESSIONS = 120;

// Expands "Mon/Wed/Fri, 08:00–12:00, 2–16 Oct" into one slot per matching
// date. Dates are plain calendar days and times are SAST wall-clock, which is
// exactly what the builder's datetime-local inputs hold, so no timezone maths
// happens here. Starts already in `existingStarts` are skipped.
export const generateRepeatingSlots = ({
  fromDate,
  toDate,
  weekdays = [],
  startTime,
  endTime,
  existingStarts = [],
}) => {
  if (!fromDate || !toDate || !startTime || !endTime || !weekdays.length) return [];
  const toUtcDay = (value) => {
    const [year, month, day] = value.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  };
  const first = toUtcDay(fromDate);
  const last = toUtcDay(toDate);
  if (Number.isNaN(first) || Number.isNaN(last) || last < first) return [];

  const wanted = new Set(weekdays.map(Number));
  const skip = new Set(existingStarts);
  const slots = [];
  for (let day = first; day <= last && slots.length < MAX_REPEAT_SESSIONS; day += 86400000) {
    const date = new Date(day);
    if (!wanted.has(date.getUTCDay())) continue;
    const isoDate = date.toISOString().slice(0, 10);
    const start = `${isoDate}T${startTime}`;
    if (skip.has(start)) continue;
    slots.push({ startTime: start, endTime: `${isoDate}T${endTime}` });
  }
  return slots;
};
